/**
 * DesktopSidebar - Collapsible overlay sidebar for desktop layout
 *
 * Features:
 * - 72px collapsed, 260px expanded
 * - Expands on hover (overlay, doesn't push content)
 * - Hidden by default (feature flag)
 * - Auto-collapses when mouse leaves
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { Colors } from '@/constants/colors';
import { ProfileSection } from './ProfileSection';
import { SidebarNavItem } from './SidebarNavItem';
import { GroupsSidebarList } from './GroupsSidebarList';
import { getUserGroups, type Group } from '@/services/groups/groupService';
import { Ionicons } from '@expo/vector-icons';
import { useNavigationHelpers } from '@/layouts/NavigationContext';
import { CreateGroupModal } from '@/components/groups/CreateGroupModal';
import { JoinGroupModal } from '@/components/groups/JoinGroupModal';

interface DesktopSidebarProps {
  visible?: boolean; // Feature flag to show/hide
}

export const DesktopSidebar: React.FC<DesktopSidebarProps> = ({
  visible = false, // Default hidden for safety
}) => {
  const navHelpers = useNavigationHelpers();
  const [isExpanded, setIsExpanded] = useState(false);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [joinModalVisible, setJoinModalVisible] = useState(false);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await getUserGroups();
    if (fetchError) {
      setError(fetchError);
      setLoading(false);
      return;
    }
    if (data) {
      setGroups(data);
    }
    setLoading(false);
  }, []);

  // Fetch groups on mount
  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  // Detect active section from current tab route (memoized)
  const activeSection = useMemo((): string => {
    const routeName = navHelpers.currentTabRoute;
    if (routeName === 'Home' || routeName === 'HomeScreen') return 'home';
    if (routeName === 'Research') return 'research';
    if (routeName === 'Watchlist') return 'watchlist';
    if (routeName === 'Groups' || routeName === 'GroupsList' || routeName === 'GroupChat')
      return 'groups';
    if (routeName === 'Feedback') return 'feedback';
    if (routeName === 'Profile') return 'profile';
    return '';
  }, [navHelpers.currentTabRoute]);

  const selectedGroupId = navHelpers.currentGroupId;

  // Navigation handlers (memoized to prevent child re-renders)
  const handleNavigateHome = useCallback(() => {
    navHelpers.navigateToHome();
  }, [navHelpers]);

  const handleNavigateResearch = useCallback(() => {
    navHelpers.navigateToResearch();
  }, [navHelpers]);

  const handleNavigateWatchlist = useCallback(() => {
    navHelpers.navigateToWatchlist();
  }, [navHelpers]);

  const handleNavigateFeedback = useCallback(() => {
    navHelpers.navigateToFeedback();
  }, [navHelpers]);

  const handleNavigateProfile = useCallback(() => {
    navHelpers.navigateToProfile();
  }, [navHelpers]);

  const handleGroupPress = useCallback(
    (groupId: string) => {
      const group = groups.find(g => g.id === groupId);
      if (group) {
        navHelpers.navigateToGroupChat(group.id, group.name);
      }
    },
    [groups, navHelpers]
  );

  const handleMouseEnter = useCallback(() => {
    setIsExpanded(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsExpanded(false);
  }, []);

  const handleCreateGroup = useCallback(() => {
    console.log('[DesktopSidebar] Create group clicked');
    setCreateModalVisible(true);
  }, []);

  const handleJoinGroup = useCallback(() => {
    console.log('[DesktopSidebar] Join group clicked');
    setJoinModalVisible(true);
  }, []);

  const handleCreateSuccess = useCallback(
    (groupId: string, groupName: string) => {
      console.log('[DesktopSidebar] Group created:', groupId, groupName);
      // Reload groups to show the new group
      loadGroups();
      // Navigate to the new group chat
      navHelpers.navigateToGroupChat(groupId, groupName);
    },
    [loadGroups, navHelpers]
  );

  const handleJoinSuccess = useCallback(
    (groupId: string) => {
      console.log('[DesktopSidebar] Successfully joined group:', groupId);
      // Reload groups to show the newly joined group
      loadGroups();
      // Find the group and navigate to it
      const joinedGroup = groups.find(g => g.id === groupId);
      if (joinedGroup) {
        navHelpers.navigateToGroupChat(joinedGroup.id, joinedGroup.name);
      }
    },
    [loadGroups, navHelpers, groups]
  );

  // Don't render if not visible (feature flag)
  if (!visible) {
    return null;
  }

  return (
    <>
      <View
        style={[styles.sidebar, isExpanded && styles.sidebarExpanded]}
        // @ts-ignore - React Native Web supports mouse events
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* FIXED TOP: Profile */}
        <ProfileSection isExpanded={isExpanded} />

        {/* FIXED TOP: Main Navigation */}
        <View style={styles.sectionFixed}>
          <SidebarNavItem
            icon="home-outline"
            label="Home"
            isActive={activeSection === 'home'}
            isExpanded={isExpanded}
            onPress={handleNavigateHome}
          />
          <SidebarNavItem
            icon="flask-outline"
            label="Research"
            isActive={activeSection === 'research'}
            isExpanded={isExpanded}
            onPress={handleNavigateResearch}
          />
          <SidebarNavItem
            icon="star-outline"
            label="Watchlist"
            isActive={activeSection === 'watchlist'}
            isExpanded={isExpanded}
            onPress={handleNavigateWatchlist}
          />
        </View>

        {/* Divider */}
        <View style={styles.divider} />

        {/* SCROLLABLE MIDDLE: Groups */}
        <View style={styles.sectionScrollable}>
          {isExpanded && <Text style={styles.sectionTitle}>GROUPS</Text>}

          <ScrollView style={styles.groupsScroll} showsVerticalScrollIndicator={false}>
            {loading ? (
              <View style={styles.centerContainer}>
                <ActivityIndicator size="small" color={Colors.accent.primary} />
                {isExpanded && <Text style={styles.statusText}>Loading groups...</Text>}
              </View>
            ) : error ? (
              <View style={styles.centerContainer}>
                <Ionicons name="alert-circle-outline" size={32} color={Colors.status.error} />
                {isExpanded && (
                  <>
                    <Text style={styles.errorText}>Failed to load groups</Text>
                    <TouchableOpacity onPress={loadGroups} style={styles.retryButton}>
                      <Text style={styles.retryText}>Retry</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            ) : groups.length === 0 ? (
              <View style={styles.centerContainer}>
                <Ionicons name="people-outline" size={32} color={Colors.text.tertiary} />
                {isExpanded && (
                  <Text style={styles.emptyText}>No groups yet{'\n'}Create or join one!</Text>
                )}
              </View>
            ) : (
              <GroupsSidebarList
                groups={groups}
                selectedGroupId={selectedGroupId}
                isExpanded={isExpanded}
                onGroupPress={handleGroupPress}
              />
            )}
          </ScrollView>

          {/* Create/Join buttons */}
          {isExpanded && (
            <View style={styles.groupActions}>
              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleCreateGroup}
                activeOpacity={0.7}
              >
                <Ionicons name="add-circle-outline" size={16} color={Colors.accent.primary} />
                <Text style={styles.actionButtonText}>Create</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleJoinGroup}
                activeOpacity={0.7}
              >
                <Ionicons name="log-in-outline" size={16} color={Colors.accent.primary} />
                <Text style={styles.actionButtonText}>Join</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {/* Divider */}
        <View style={styles.divider} />

        {/* FIXED BOTTOM: Utility */}
        <View style={styles.sectionFixed}>
          <SidebarNavItem
            icon="megaphone-outline"
            label="Feedback"
            isActive={activeSection === 'feedback'}
            isExpanded={isExpanded}
            onPress={handleNavigateFeedback}
          />
          <SidebarNavItem
            icon="settings-outline"
            label="Profile"
            isActive={activeSection === 'profile'}
            isExpanded={isExpanded}
            onPress={handleNavigateProfile}
          />
        </View>
      </View>

      {/* Create Group Modal */}
      <CreateGroupModal
        visible={createModalVisible}
        onClose={() => setCreateModalVisible(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Join Group Modal */}
      <JoinGroupModal
        visible={joinModalVisible}
        onClose={() => setJoinModalVisible(false)}
        onSuccess={handleJoinSuccess}
      />
    </>
  );
};

const styles = StyleSheet.create({
  sidebar: {
    width: 72,
    height: '100vh' as any, // React Native Web supports vh
    backgroundColor: Colors.background.secondary,
    borderRightWidth: 1,
    borderRightColor: Colors.border.subtle,
    position: 'absolute' as any,
    left: 0,
    top: 0,
    zIndex: 100,
    flexDirection: 'column',
    // @ts-ignore - Web-only property
    transition: 'width 200ms ease-in-out',
  },
  sidebarExpanded: {
    width: 260,
    // @ts-ignore - Web-only property
    boxShadow: '4px 0 12px rgba(0, 0, 0, 0.5)',
  },
  sectionFixed: {
    flexShrink: 0,
  },
  sectionScrollable: {
    flex: 1,
    minHeight: 0,
  },
  divider: {
    height: 1,
    backgroundColor: Colors.border.subtle,
    marginVertical: 8,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: '700',
    color: Colors.text.tertiary,
    textTransform: 'uppercase',
    letterSpacing: 1,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  groupsScroll: {
    flex: 1,
  },
  groupActions: {
    flexDirection: 'row',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: Colors.background.tertiary,
    borderRadius: 6,
    // @ts-ignore - Web-only property
    cursor: 'pointer',
  },
  actionButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.accent.primary,
  },
  centerContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 32,
    paddingHorizontal: 16,
  },
  statusText: {
    marginTop: 12,
    fontSize: 12,
    color: Colors.text.tertiary,
    textAlign: 'center',
  },
  errorText: {
    marginTop: 12,
    fontSize: 12,
    color: Colors.status.error,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 12,
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: Colors.background.tertiary,
    borderRadius: 4,
  },
  retryText: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.accent.primary,
  },
  emptyText: {
    marginTop: 12,
    fontSize: 12,
    color: Colors.text.tertiary,
    textAlign: 'center',
    lineHeight: 18,
  },
});
